import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SCRAPERS } from "@/lib/constants";
import MemberCard from "@/components/ledger/MemberCard";

// Same portal-label idiom as LedgerDashboardPage.jsx (kept local — that file's own
// portalLabel() isn't exported, and the acceptance contract for this task limits the
// page's diff to its cascade branch/imports/editable gates, not new exports).
const PORTAL_DISPLAY_NAMES = Object.fromEntries(SCRAPERS.map((s) => [s.id, s.name]));
PORTAL_DISPLAY_NAMES._unattributed = "Email";
const portalLabel = (id) => PORTAL_DISPLAY_NAMES[id] || id;

function safeCurrencyCode(currency) {
  const n = String(currency || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(n) ? n : "USD";
}
function fmtMoney(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: safeCurrencyCode(currency), minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}

// Never present a blended cross-currency total: when the portal's members disagree on
// currency, total_nav is null (see bookTree.buildBookTree) and this renders one amount per
// currency, largest first, as "X ($) - Y (NIS) - Z (GBP)" — never a blended number.
const CCY_LABEL = { USD: "$", ILS: "NIS", EUR: "€", GBP: "GBP" };
const ccyLabel = (ccy) => {
  const c = String(ccy || "").trim().toUpperCase();
  return CCY_LABEL[c] || c || "?";
};
const fmtPlain = (v) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(v);
function fmtNavByCcyChips(navByCcy) {
  const entries = Object.entries(navByCcy || {}).filter(([, v]) => v != null);
  if (entries.length === 0) return "—";
  entries.sort((a, b) => Math.abs(Number(b[1])) - Math.abs(Number(a[1])));
  return entries.map(([ccy, v]) => `${fmtPlain(v)} (${ccyLabel(ccy)})`).join(" - ");
}

/**
 * PortalCard — top level of the position-book cascading tree (Portal → Member → Fund),
 * active only when LedgerDashboardPage renders data.source === "position_book" (see
 * vault/90-Active/PLAN-position-book-ledger-tree.md). Same shadcn Card/CardContent/Badge
 * idiom as FundRow (LedgerDashboardPage.jsx) — collapsed shows the portal label, total NAV,
 * and a member-count badge; expands to one MemberCard per investor.
 *
 * Props:
 *   portalNode  — { portal, total_nav, member_count, members[] } from bookTree.buildBookTree
 *   autoExpand  — seeds the initial open state (still user-collapsible afterwards) —
 *                 LedgerDashboardPage derives this from the active fund/investor/search filter
 *   fundSort    — current "Sort by" metric key, forwarded to each MemberCard
 *   FundRowComponent — the page's own FundRow function, passed down as a prop (never
 *                 imported directly — LedgerDashboardPage.jsx already imports this file,
 *                 so importing FundRow back from the page would be a circular import;
 *                 passing the component down as a prop sidesteps that entirely)
 *   fundRowProps — everything the innermost <FundRow> needs, forwarded unchanged all the way
 *                 down (editable, compareSet, onCompareToggle, onDiscard, mergeModeActive/
 *                 mergeSelection/onMergeToggle/onUnmergeFund, linkMode/linkGroup/onLinkToggle/
 *                 onUnlinkMember) — book mode ships editable:false so every mutation
 *                 affordance stays hidden, same gate as the classic render.
 */
export default function PortalCard({ portalNode, autoExpand = false, fundSort, FundRowComponent, fundRowProps }) {
  const [open, setOpen] = useState(autoExpand);

  // Seed (not force) the open state from the active filter — once opened this way the
  // user can still collapse it; this effect only re-fires when autoExpand itself flips.
  useEffect(() => {
    if (autoExpand) setOpen(true);
  }, [autoExpand]);

  const { portal, total_nav, nav_by_ccy, member_count, members } = portalNode;

  // Best-effort display currency for the portal-level NAV figure: the single currency
  // shared by every member rollup, else fall back to USD (no portal-level currency
  // contract exists — bookTree's rollup only promises total_nav + member_count).
  const currencies = new Set(
    (members || []).map((m) => m.rollup?.currency).filter((c) => c && c !== "mixed")
  );
  const displayCurrency = currencies.size === 1 ? [...currencies][0] : "USD";

  return (
    <Card className="mb-3 max-w-full" data-testid={`portal-card-${portal}`}>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 min-w-0 text-left"
        >
          {open ? <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-bold text-gray-900 truncate">{portalLabel(portal)}</h2>
              <Badge variant="secondary" className="text-[10px] flex-shrink-0">
                {member_count} {member_count === 1 ? "member" : "members"}
              </Badge>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-0.5 text-xs text-gray-500">
              <span>
                Current Value{" "}
                <span className="font-mono text-gray-900">
                  {total_nav == null ? fmtNavByCcyChips(nav_by_ccy) : fmtMoney(total_nav, displayCurrency)}
                </span>
              </span>
            </div>
          </div>
        </button>

        {open && (
          <div className="border-t border-gray-100 pl-4">
            {(members || []).map((memberNode) => (
              <MemberCard
                key={`${portal}|${memberNode.investor_name}`}
                memberNode={memberNode}
                portalId={portal}
                autoExpand={autoExpand}
                fundSort={fundSort}
                FundRowComponent={FundRowComponent}
                fundRowProps={fundRowProps}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
